import { convert } from "portal/utils/units/units";
import {
  DEFAULT_IMPLEMENT_WIDTH_MM,
  MINIMUM_IMPLEMENT_WIDTH_MM,
} from "portal/utils/robots";
import { Feature, Polygon } from "geojson";
import { getWidth, NormalizedBlock } from "portal/utils/blocks";
import {
  isPointProbablyWrong,
  isSpatialPositionProbablyWrong,
  Point,
} from "portal/utils/geo";
import { point, polygon, Properties, Units } from "@turf/helpers";
import { SpatialPosition } from "protos/metrics/metrics";
import distance from "@turf/distance";
import getBearing from "@turf/bearing";
import transformTranslate from "@turf/transform-translate";

export interface BlockToPolygonOptions {
  properties?: Properties;
  units: Units;
}

type PositionedBlock = NormalizedBlock & {
  start: SpatialPosition;
  end: SpatialPosition;
};

const isUsableCoordinate = (value: number): boolean =>
  Number.isFinite(value) && value !== 0;

/**
 * Whether a block has a usable, plausible position — i.e. it's safe to plot
 * on the map or include in an export. Shared by `useMapBlocks` (rendering)
 * and `buildJobInsightsGeoJson` (export) so the two stay in sync by
 * construction instead of by convention.
 */
export const isBlockRenderable = (
  block: NormalizedBlock
): block is PositionedBlock =>
  !block.suspicious &&
  block.start !== undefined &&
  block.end !== undefined &&
  !isSpatialPositionProbablyWrong(block.start) &&
  !isSpatialPositionProbablyWrong(block.end) &&
  isUsableCoordinate(block.start.latitude) &&
  isUsableCoordinate(block.start.longitude) &&
  isUsableCoordinate(block.end.latitude) &&
  isUsableCoordinate(block.end.longitude);

/**
 * Convert a normalized block into a GeoJSON swath Polygon (WGS84 lon/lat).
 *
 * Prefers the block's four measured corners; falls back to extruding the
 * center line by the implement width. Returns `undefined` for suspicious
 * blocks, blocks with probably-wrong coordinates, or blocks lacking the data
 * needed to form a polygon.
 *
 * Extracted from the map worker so it can be reused synchronously off the main
 * thread (e.g. the Job Insights GeoJSON export). Must stay worker-safe: no
 * `self`/`postMessage`/DOM references.
 */
export const blockToPolygon = (
  block: NormalizedBlock,
  { properties, units }: BlockToPolygonOptions
): Feature<Polygon> | undefined => {
  if (block.suspicious) {
    console.warn("Skipping suspicious block");
    return;
  }

  let widthMm: number;
  let trustCorners = true;
  if (
    block.originalBlock.type === "old" &&
    block.originalBlock.block.block?.implementWidthData?.widthMm
  ) {
    widthMm = block.originalBlock.block.block.implementWidthData.widthMm;
  } else if (
    block.originalBlock.type === "jigsaw" &&
    block.originalBlock.block.BlockWidthMm
  ) {
    widthMm = block.originalBlock.block.BlockWidthMm;
  } else {
    widthMm =
      getWidth(
        block.originalBlock.type === "old"
          ? block.originalBlock.block.block
          : block.originalBlock.block,
        "millimeters"
      ) ?? DEFAULT_IMPLEMENT_WIDTH_MM;
    if (widthMm < MINIMUM_IMPLEMENT_WIDTH_MM) {
      // TODO: there's a bug with units - meters are being reported as mm in the lat/long calcs in jigsaw. Fixing it in post for the sake of testing.
      // This worksround does not need to exist long-term - it shoudl be fixed beore the first fully valid jigsaw metrics.
      // If it's not cleaned up before merge, feel free to remove entirely once the bug is resolved.
      // Tracked with SOFTWARE-5840
      trustCorners = false;
      widthMm *= 1000;
    }
  }

  const positionInfo =
    block.originalBlock.type === "old"
      ? block.originalBlock.block.block
      : block.originalBlock.block;

  if (positionInfo === undefined) {
    return;
  }

  const _width = convert(widthMm).from("mm").to("ft");
  properties = { ...properties, _width };

  if (
    positionInfo.startLeft &&
    positionInfo.startRight &&
    positionInfo.endLeft &&
    positionInfo.endRight &&
    trustCorners
  ) {
    // we have all for corners, just use those
    if (
      isSpatialPositionProbablyWrong(positionInfo.startLeft) ||
      isSpatialPositionProbablyWrong(positionInfo.startRight) ||
      isSpatialPositionProbablyWrong(positionInfo.endLeft) ||
      isSpatialPositionProbablyWrong(positionInfo.endRight)
    ) {
      console.warn("Block parameters are probably wrong");
      return;
    }

    return polygon(
      [
        [
          [positionInfo.startLeft.longitude, positionInfo.startLeft.latitude],
          [positionInfo.startRight.longitude, positionInfo.startRight.latitude],
          [positionInfo.endRight.longitude, positionInfo.endRight.latitude],
          [positionInfo.endLeft.longitude, positionInfo.endLeft.latitude],
          [positionInfo.startLeft.longitude, positionInfo.startLeft.latitude],
        ],
      ],
      {
        ...properties,
        _length: distance(
          [positionInfo.startLeft.longitude, positionInfo.startLeft.latitude],
          [positionInfo.endLeft.longitude, positionInfo.endLeft.latitude],
          { units: "feet" }
        ),
      }
    );
  } else if (block.start && block.end) {
    // we get the center line, use the width to calculate the rest
    const startPoint: Point = [block.start.longitude, block.start.latitude];
    const endPoint: Point = [block.end.longitude, block.end.latitude];

    if (isPointProbablyWrong(startPoint) || isPointProbablyWrong(endPoint)) {
      console.warn("Block parameters are probably wrong");
      return;
    }

    const bearing = getBearing(startPoint, endPoint);
    const startLeft = transformTranslate(
      point(endPoint),
      widthMm / 2,
      bearing - 90,
      { units }
    );
    const startRight = transformTranslate(
      point(endPoint),
      widthMm / 2,
      bearing + 90,
      { units }
    );
    const endLeft = transformTranslate(
      point(startPoint),
      widthMm / 2,
      bearing - 90,
      { units }
    );
    const endRight = transformTranslate(
      point(startPoint),
      widthMm / 2,
      bearing + 90,
      { units }
    );

    return polygon(
      [
        [
          startLeft.geometry.coordinates,
          startRight.geometry.coordinates,
          endRight.geometry.coordinates,
          endLeft.geometry.coordinates,
          startLeft.geometry.coordinates,
        ],
      ],
      properties
    );
  } else {
    console.warn("Block has no parameters");
  }
};
